import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { useNavigate } from "react-router-dom";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import filter from "lodash/filter";
import keys from "lodash/keys";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import reduce from "lodash/reduce";

import UnitItem from "./UnitItem";
import get from "lodash/get";
import Button from "../shared/Button";
import { useFormValuesContext } from "../context/FormValuesContext";
import { useQuery } from "@tanstack/react-query";
import EstateService from "../../services/EstateService";

const SelectDuplicatedUnitsForm = ({}) => {
  const navigate = useNavigate();

  const { values } = useFormValuesContext();

  const { estate, estateId, officeId, officeName } = useMemo(() => {
    const estate = get(values, "estate");
    const estateId = get(estate, "id");
    const officeId = get(values, "office");
    const officeName = get(values, ["officeData", "name"], "");

    return {
      estate,
      estateId,
      officeId,
      officeName,
    };
  }, [values]);

  const { data: estates } = useQuery({
    initialData: [],
    queryKey: ["duplicate-units", estateId, officeId],
    queryFn: async () => {
      const { city, zip, category } = pick(estate, ["city", "zip", "category"]);
      const categoryId = get(category, "id");

      const data = await EstateService.getEstates({
        purpose: 1,
        categories: [categoryId],
        representatives: [9486, 48343],
        zipCodes: [zip],
        city,
        offices: [officeId],
      });
      return data.estates;
    },
  });

  const result = useMemo(() => {
    if (estates.length === 0) return [];

    const extractKeywords = (str) => {
      const words = str
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .split(/\s+/);
      return words.filter(Boolean);
    };

    const findResult = (keywords) => {
      const result = [];
      const normalizedKeywords = keywords.map((mot) =>
        mot.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      );

      for (const estate of estates) {
        const estateName = get(estate, "name")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        for (const keyword of normalizedKeywords) {
          if (estateName.includes(keyword)) {
            result.push(estate);
            break;
          }
        }
      }
      return result;
    };

    return findResult(extractKeywords(estate.name));
  }, [estates]);

  const [selectedUnits, setSelectedUnits] = useState({});

  useEffect(() => {
    if (!isEmpty(result)) {
      const tempSelected = reduce(
        result,
        (acc, curr) => {
          acc[get(curr, "id")] = true;
          return acc;
        },
        {}
      );
      setSelectedUnits(tempSelected);
    }
  }, [result]);

  const handleSelectUnit = (unitId) => (value) =>
    setSelectedUnits((prev) => ({
      ...prev,
      [unitId]: value,
    }));

  const onSubmit = (includesSelectedUnits) => {
    const { startDate, endDate } = values;
    const filteredSelectedUnits = filter(
      keys(selectedUnits),
      (unitId) => !!selectedUnits[unitId]
    );

    const baseUrl = `/new-document/${estateId}`;
    const startDateStr = dayjs(startDate).locale("fr").format("L");
    const endDateStr = dayjs(endDate).locale("fr").format("L");
    const searchParams = new URLSearchParams({
      ...pick(values, ["documentType", "owner"]),
      startDate: startDateStr,
      endDate: endDateStr,
      selectedUnits: includesSelectedUnits ? filteredSelectedUnits : [],
    });
    const url = `${baseUrl}?${searchParams.toString()}`;
    navigate(url);
  };

  return (
    <StyleWrapper>
      <Stack className="rounded-box" gap="20px">
        <Stack
          className="header-container"
          flexDirection="row"
          justifyContent="space-between"
        >
          <p className="title">
            Liste des biens similaires présents dans le bureau
          </p>
          <p className="office-name text-ellipsis">{officeName}</p>
        </Stack>
        <p className="description-txt">
          Les actions et historiques des biens sélectionnés seront ajoutés au
          rapport promoteur.
        </p>
      </Stack>
      <Stack className="rounded-box d-flex">
        {result.map((estate) => {
          const estateId = get(estate, "id");
          return (
            <UnitItem
              key={estateId}
              estate={estate}
              hasValue={selectedUnits[estateId] !== undefined}
              isSelected={selectedUnits[estateId]}
              handleSelectUnit={handleSelectUnit(estateId)}
            />
          );
        })}
      </Stack>
      <Stack
        className="modal-action-container"
        flexDirection="row"
        justifyContent="center"
        gap={2}
        sx={{ mt: 2 }}
      >
        <Button className="ignore-btn" onClick={() => onSubmit(false)}>
          Ignorer cette étape
        </Button>
        <Button
          className="confirm-btn"
          color="primary"
          variant="contained"
          onClick={() => onSubmit(true)}
        >
          Confirmer
        </Button>
      </Stack>
    </StyleWrapper>
  );
};

const StyleWrapper = styled.div`
  .rounded-box:nth-child(1) {
    background-color: var(--light-grey-bg);
    padding: 20px 40px;
    margin-bottom: 20px;
  }

  .rounded-box:nth-child(2) {
    background-color: var(--light-grey-bg);
    max-height: 542px;
    width: 991px;
    overflow-y: auto;
    gap: 10px;
  }

  .header-container {
    border-bottom: 1px solid var(--border-color);
  }

  .title {
    font-size: 26px;
    margin-bottom: 10px;
    max-width: 530px;
  }

  .office-name {
    color: var(--red-color);
    font-size: 21px;
    font-weight: 700;
    text-align: center;
    background-color: var(--white);
    height: fit-content;
    margin-bottom: 0;
    padding: 7px 5px;
    width: 325px;
  }

  .description-txt {
    font-size: 14px;
    font-weight: 500;
  }

  .modal-action-container button {
    width: 200px;
    font-size: 14px;
    font-weight: 700;
  }
`;

export default SelectDuplicatedUnitsForm;
